import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, Linking, TouchableOpacity, Modal, TextInput, ScrollView } from 'react-native';
import { platformAlert } from '../utils/platformAlert';
import { Image } from 'expo-image';
import { MaterialIcons, FontAwesome5, Feather } from '@expo/vector-icons';
import { useDeliveryTracking } from '../hooks/use-delivery-tracking';
import { useRefundRequest } from '../hooks/use-refund-request';
import type { DeliveryStatus } from '../types/delivery';
import { spacing } from '../theme';
import { useColors, ThemeColors } from '../hooks/useColors';
import { supabase } from '../lib/supabase';
import { InAppBrowserModal } from './ui/InAppBrowserModal';

interface DeliveryTrackerProps {
  deliveryId: string;
  isNewDelivery?: boolean;
}

export function DeliveryTracker({ deliveryId, isNewDelivery }: DeliveryTrackerProps) {
  const colors = useColors();
  const styles = createStyles(colors);
  const { deliveryStatus, loading, error, refreshStatus } = useDeliveryTracking(deliveryId);
  const { requestRefund, isLoading: isRefundLoading } = useRefundRequest({ deliveryId });
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showProofModal, setShowProofModal] = useState(false);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [status, setStatus] = useState<DeliveryStatus | null>(null);

  useEffect(() => {
    let subscription: any;

    const setupSubscription = async () => {
      try {
        // Initial fetch
        const { data: delivery, error: fetchError } = await supabase
          .from('deliveries')
          .select('*')
          .eq('delivery_id', deliveryId)
          .single();

        if (fetchError) throw fetchError;
        if (delivery) {
          const deliveryStatus: DeliveryStatus = {
            status: delivery.status,
            pickup_eta: delivery.pickup_eta,
            dropoff_eta: delivery.dropoff_eta,
            courier: delivery.courier,
            tracking_url: delivery.tracking_url
          };
          setStatus(deliveryStatus);
        }

        // Subscribe to changes
        subscription = supabase
          .channel(`delivery-${deliveryId}`)
          .on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'deliveries',
              filter: `delivery_id=eq.${deliveryId}`,
            },
            (payload) => {
              const delivery = payload.new;
              const deliveryStatus: DeliveryStatus = {
                status: delivery.status,
                pickup_eta: delivery.pickup_eta,
                dropoff_eta: delivery.dropoff_eta,
                courier: delivery.courier,
                tracking_url: delivery.tracking_url
              };
              setStatus(deliveryStatus);
            }
          )
          .subscribe();

      } catch (err) {
        console.error('Error setting up subscription:', err);
      }
    };

    setupSubscription();

    return () => {
      if (subscription) {
        supabase.removeChannel(subscription);
      }
    };
  }, [deliveryId]);

  const getStatusIcon = () => {
    switch (status?.status) {
      case 'pending':
        return <MaterialIcons name="watch-later" size={24} color="#f59e0b" />;
      case 'pickup_pending':
        return <MaterialIcons name="delivery-dining" size={24} color="#00D488" />;
      case 'pickup_complete':
        return <MaterialIcons name="check-circle" size={24} color="#00A86B" />;
      case 'in_transit':
        return <MaterialIcons name="local-shipping" size={24} color="#00D488" />;
      case 'delivered':
        return <MaterialIcons name="verified" size={24} color="#00A86B" />;
      case 'cancelled':
        return <MaterialIcons name="cancel" size={24} color="#F43F5E" />;
      default:
        return <MaterialIcons name="help-outline" size={24} color="#6b7280" />;
    }
  };

  const getStatusText = () => {
    switch (status?.status) {
      case 'pending':
        return 'Waiting for courier assignment';
      case 'pickup_pending':
        return 'Courier is heading to pickup location';
      case 'pickup_complete':
        return 'Pickup completed, heading to you';
      case 'in_transit':
        return 'Courier is heading to your location';
      case 'delivered':
        return 'Delivery completed';
      case 'cancelled':
        return 'Delivery was cancelled';
      default:
        return 'Unknown status';
    }
  };

  const handleCallCourier = () => {
    if (status?.courier?.phone) {
      Linking.openURL(`tel:${status.courier.phone}`);
    }
  };

  const handleMessageCourier = () => {
    if (status?.courier?.phone) {
      Linking.openURL(`sms:${status.courier.phone}`);
    }
  };

  const handleRequestRefund = async () => {
    try {
      if (!refundReason || !refundAmount) {
        return;
      }

      const amount = parseFloat(refundAmount);
      if (isNaN(amount) || amount <= 0) {
        return;
      }

      await requestRefund({
        reason: refundReason,
        refund_items: [{
          name: 'Delivery Fee',
          quantity: 1,
          amount: amount
        }],
        party_at_fault: 'PARTNER' // Default to partner fault
      });

      setShowRefundModal(false);
      refreshStatus();
    } catch (error) {
      console.error('Error requesting refund:', error);
    }
  };

  if (isNewDelivery) {
    return (
      <View style={styles.container}>
        <View style={styles.successContainer}>
          <MaterialIcons name="check-circle" size={48} color="#00A86B" />
          <Text style={styles.successTitle}>Delivery Created Successfully!</Text>
          <View style={styles.successDetails}>
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Delivery ID:</Text>
              <Text style={styles.detailValue}>{deliveryId}</Text>
            </View>
          </View>
        </View>
      </View>
    );
  }

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#00D488" />
        <Text style={styles.loadingText}>Loading delivery status...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.errorContainer}>
        <MaterialIcons name="error-outline" size={24} color="#F43F5E" />
        <Text style={styles.errorText}>Error loading delivery status</Text>
        <Text style={styles.errorDetails}>{error.message}</Text>
        <TouchableOpacity style={styles.retryButton} onPress={refreshStatus}>
          <Text style={styles.retryText}>Retry</Text>
        </TouchableOpacity>
      </View>
    );
  }

  if (!status) {
    return (
      <View style={styles.notFoundContainer}>
        <MaterialIcons name="search-off" size={24} color="#6b7280" />
        <Text style={styles.notFoundText}>Delivery not found</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.statusHeader}>
        {getStatusIcon()}
        <Text style={styles.statusText}>{getStatusText()}</Text>
      </View>

      {(status.status === 'pickup_pending' || 
        status.status === 'pickup_complete' || 
        status.status === 'in_transit') && (
        <View style={styles.courierContainer}>
          <View style={styles.courierHeader}>
            <FontAwesome5 name="user-astronaut" size={20} color="#4b5563" />
            <Text style={styles.courierName}>
              {status.courier?.name || 'Your Courier'}
            </Text>
          </View>

          {status.courier?.vehicle?.type && (
            <View style={styles.vehicleInfo}>
              <FontAwesome5 
                name={
                  status.courier.vehicle.type === 'bicycle' ? 'bicycle' : 
                  status.courier.vehicle.type === 'scooter' ? 'motorcycle' : 
                  status.courier.vehicle.type === 'car' ? 'car-alt' : 'truck'
                } 
                size={16} 
                color="#6b7280" 
              />
              <Text style={styles.vehicleText}>
                {status.courier.vehicle.make} {status.courier.vehicle.model} 
                {status.courier.vehicle.color && ` (${status.courier.vehicle.color})`}
              </Text>
            </View>
          )}

          <View style={styles.etaContainer}>
            <FontAwesome5 name="clock" size={16} color="#6b7280" />
            <Text style={styles.etaText}>
              {status.status === 'in_transit' 
                ? `Estimated arrival: ${new Date(status.dropoff_eta || '').toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`
                : `Pickup ETA: ${new Date(status.pickup_eta || '').toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`
              }
            </Text>
          </View>

          <View style={styles.actionButtons}>
            {status.courier?.phone && (
              <>
                <TouchableOpacity style={[styles.actionButton, { marginRight: 4 }]} onPress={handleCallCourier}>
                  <FontAwesome5 name="phone" size={16} color="#ffffff" />
                  <Text style={styles.actionButtonText}>Call</Text>
                </TouchableOpacity>

                <TouchableOpacity style={[styles.actionButton, { marginHorizontal: 4 }]} onPress={handleMessageCourier}>
                  <FontAwesome5 name="comment" size={16} color="#ffffff" />
                  <Text style={styles.actionButtonText}>Message</Text>
                </TouchableOpacity>
              </>
            )}
            
            <TouchableOpacity 
              style={[styles.actionButton, { marginLeft: status.courier?.phone ? 4 : 0 }]} 
              onPress={() => {
                if (status?.tracking_url) {
                  setShowTrackingModal(true);
                }
              }}
            >
              <FontAwesome5 name="map-marked-alt" size={16} color="#ffffff" />
              <Text style={styles.actionButtonText}>Track Order</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      <View style={styles.refreshContainer}>
        <Text style={styles.lastUpdatedText}>
          Last updated: {new Date(status.last_updated || '').toLocaleTimeString()}
        </Text>
        <TouchableOpacity style={styles.refreshButton} onPress={refreshStatus}>
          <FontAwesome5 name="sync" size={14} color="#00D488" />
          <Text style={styles.refreshText}>Refresh</Text>
        </TouchableOpacity>
      </View>

      {status?.status === 'delivered' && (
        <View style={styles.deliveredActions}>
          <TouchableOpacity 
            style={[styles.actionButton, { marginTop: 8, flex: 1, marginRight: 4 }]} 
            onPress={() => setShowRefundModal(true)}
          >
            <FontAwesome5 name="money-bill-wave" size={16} color="#ffffff" />
            <Text style={styles.actionButtonText}>Request Refund</Text>
          </TouchableOpacity>

          {status.proof_of_delivery && (
            <TouchableOpacity 
              style={[styles.actionButton, { marginTop: 8, flex: 1, marginLeft: 4 }]} 
              onPress={() => setShowProofModal(true)}
            >
              <FontAwesome5 name="image" size={16} color="#ffffff" />
              <Text style={styles.actionButtonText}>View Proof</Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      <Modal
        visible={showRefundModal}
        animationType="slide"
        transparent={true}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Request Refund</Text>
            
            <TextInput
              style={styles.input}
              placeholder="Reason for refund"
              value={refundReason}
              onChangeText={setRefundReason}
              multiline
            />

            <TextInput
              style={styles.input}
              placeholder="Refund amount ($)"
              value={refundAmount}
              onChangeText={setRefundAmount}
              keyboardType="decimal-pad"
            />

            <View style={styles.modalButtons}>
              <TouchableOpacity 
                style={[styles.modalButton, styles.cancelButton]} 
                onPress={() => setShowRefundModal(false)}
              >
                <Text style={styles.modalButtonText}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity 
                style={[styles.modalButton, styles.submitButton]} 
                onPress={handleRequestRefund}
                disabled={isRefundLoading}
              >
                {isRefundLoading ? (
                  <ActivityIndicator size="small" color="#ffffff" />
                ) : (
                  <Text style={styles.modalButtonText}>Submit</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={showProofModal}
        animationType="slide"
        transparent={true}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Proof of Delivery</Text>
              <TouchableOpacity 
                style={styles.closeButton} 
                onPress={() => setShowProofModal(false)}
              >
                <MaterialIcons name="close" size={24} color="#6b7280" />
              </TouchableOpacity>
            </View>

            {status?.proof_of_delivery && (
              <>
                <Image
                  source={{ uri: status.proof_of_delivery.photo_url }}
                  style={styles.proofImage}
                  contentFit="contain"
                  cachePolicy="memory-disk"
                  transition={0}
                />
                <Text style={styles.proofTimestamp}>
                  Delivered at: {new Date(status.proof_of_delivery.timestamp).toLocaleString()}
                </Text>
                {status.proof_of_delivery.notes && (
                  <Text style={styles.proofNotes}>
                    Notes: {status.proof_of_delivery.notes}
                  </Text>
                )}
              </>
            )}
          </View>
        </View>
      </Modal>

      <InAppBrowserModal
        visible={showTrackingModal}
        url={status?.tracking_url || null}
        title="Delivery Tracking"
        onClose={() => setShowTrackingModal(false)}
      />
    </View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 16,
    marginVertical: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  statusHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  statusText: {
    fontSize: 18,
    fontWeight: '600',
    marginLeft: 8,
    color: '#1f2937',
  },
  courierContainer: {
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
  },
  courierHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  courierName: {
    fontSize: 16,
    fontWeight: '500',
    marginLeft: 8,
    color: '#1f2937',
  },
  vehicleInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  vehicleText: {
    marginLeft: 8,
    color: '#4b5563',
    fontSize: 14,
  },
  etaContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  etaText: {
    marginLeft: 8,
    color: '#4b5563',
    fontSize: 14,
  },
  actionButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  actionButton: {
    backgroundColor: '#00A86B', // jade green
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    marginHorizontal: 4,
  },
  actionButtonText: {
    color: '#ffffff',
    fontWeight: '500',
    marginLeft: 6,
    fontSize: 14,
  },
  refreshContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
  },
  lastUpdatedText: {
    fontSize: 12,
    color: '#6b7280',
  },
  refreshButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  refreshText: {
    color: '#00D488', // jade green (bright)
    marginLeft: 4,
    fontSize: 14,
    fontWeight: '500',
  },
  loadingContainer: {
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 8,
    color: '#6b7280',
    fontSize: 14,
  },
  errorContainer: {
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorText: {
    marginTop: 8,
    color: '#F43F5E',
    fontSize: 14,
  },
  errorDetails: {
    marginTop: 8,
    color: '#6b7280',
    fontSize: 12,
  },
  retryButton: {
    marginTop: 12,
    padding: 8,
    backgroundColor: '#f3f4f6',
    borderRadius: 4,
  },
  retryText: {
    color: '#00D488', // jade green (bright)
    fontWeight: '500',
  },
  notFoundContainer: {
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  notFoundText: {
    marginTop: 8,
    color: '#6b7280',
    fontSize: 14,
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 20,
    width: '100%',
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginBottom: 16,
    color: '#1f2937',
  },
  input: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    padding: 12,
    marginBottom: 12,
    fontSize: 16,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 16,
  },
  modalButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    marginLeft: 8,
  },
  cancelButton: {
    backgroundColor: '#e5e7eb',
  },
  submitButton: {
    backgroundColor: '#00A86B', // jade green
  },
  modalButtonText: {
    color: '#ffffff',
    fontWeight: '500',
    fontSize: 14,
  },
  deliveredActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  closeButton: {
    padding: 4,
  },
  proofImage: {
    width: '100%',
    height: 300,
    borderRadius: 8,
    marginBottom: 12,
    backgroundColor: '#f3f4f6',
  },
  proofTimestamp: {
    fontSize: 14,
    color: '#4b5563',
    marginBottom: 8,
  },
  proofNotes: {
    fontSize: 14,
    color: '#4b5563',
    fontStyle: 'italic',
  },
  successContainer: {
    alignItems: 'center',
    padding: 20,
  },
  successTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#00A86B', // jade green
    marginTop: 16,
    marginBottom: 24,
  },
  successDetails: {
    width: '100%',
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  detailLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#4b5563',
    marginRight: 8,
  },
  detailValue: {
    fontSize: 14,
    color: '#1f2937',
    flex: 1,
  },
}); 